import Cta from "@components/Cta/Cta";
import Grid from "@components/Grid/Grid";
import { NextContentfulImage } from "@components/Image";
import { BREAKPOINTS } from "@customTypes/Breakpoints";
import { GeneralLink } from "@customTypes/generalLink";
import { ImageWrapperType } from "@customTypes/imageWrapper";
import { TinyMceRichText } from "@customTypes/TinyMceRichText";
import { fetchApi } from "@utilities/fetchApi";
import clsx from "clsx";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { Pagination } from "swiper/modules";
import { Swiper as Slider, SwiperSlide } from "swiper/react";
import "swiper/scss";
import "swiper/scss/pagination";
import styles from "./C29_NavigationalEventsListing.module.scss";

import { EventDetailProps } from "@components/C37.5_EventDetail/C37.5_EventDetail";
import RichTextDisplay from "@components/RichTextDisplay/RichTextDisplay";

const DEFAULT_SAMPLE_SIZE = 3;

export interface NavigationalEventsListingProps {
  eventsListingTitle?: string;
  eventsListingImage?: ImageWrapperType;
  description?: TinyMceRichText;
  contentTinyMce: TinyMceRichText;
  seeAllEventsLink?: GeneralLink;
  limitEventsShown: number;
  eventForValues: string[];
  eventLocationValues: string[];
  region: string[];
  eventOrganisedByValues: string[];
  topic: string[];
}

const EventTile = dynamic(() =>
  import("@components/C19_20_Tiles/Tiles").then((mod) => mod.EventTile)
);

export const NavigationalEventsListing = (props: {
  content: NavigationalEventsListingProps;
}) => {
  const { content } = props;
  const {
    eventsListingTitle,
    eventsListingImage,
    description,
    contentTinyMce,
    seeAllEventsLink,
    limitEventsShown,
    eventForValues,
    eventLocationValues,
    region,
    eventOrganisedByValues,
    topic,
  } = content;
  const [events, setEvents] = useState<EventDetailProps[]>([]);
  const rowTags = [
    ...(eventForValues || []),
    ...(eventLocationValues || []),
    ...(region || []),
    ...(eventOrganisedByValues || []),
    ...(topic || []),
  ];
  const sampleSize = limitEventsShown || DEFAULT_SAMPLE_SIZE;

  useEffect(() => {
    const getData = async () => {
      const eventItems = await fetchApi("/api/getEvents", {
        tags: rowTags.map((tag) => tag.split(":").pop()).join(","),
        limit: `${sampleSize}`,
      });

      setEvents(eventItems);
    };
    getData();
  }, []);

  const sliderSettings = {
    slidesPerView: 1,
    className: `${styles["slider"]} navigational-events-listing-carousel`,
    allowTouchMove: true,
    pagination: {
      clickable: true,
    },
    breakpoints: {
      [BREAKPOINTS.TABLET]: {
        slidesPerView: events.length < 3 ? events.length : 3,
      },
    },
    modules: [Pagination],
  };

  return (
    <div
      className={clsx(
        "component",
        styles["navigational-events-listing"],
        styles.blue
      )}
      data-item={sampleSize}
    >
      {eventsListingImage && (
        <div className={clsx(styles["wrapper-wide"], "wrapper-header")}>
          <Grid row>
            <Grid column sm={12}>
              <div className={styles["header-image"]}>
                <NextContentfulImage
                  alt={eventsListingImage.fields.altText}
                  src={eventsListingImage.fields.url}
                  height={eventsListingImage.fields.height}
                  width={eventsListingImage.fields.width}
                />
              </div>
            </Grid>
          </Grid>
        </div>
      )}
      <div className={clsx(styles["wrapper-wide"], styles["wrapper-heading"])}>
        <div className={clsx(styles.wrapper, "wrapper")}>
          <Grid row>
            <Grid column sm={9}>
              <h2 className={styles.headline}>
                <span>{eventsListingTitle}</span>
              </h2>
              <div className={styles["supporting-copy"]}>
                <RichTextDisplay
                  richText={description}
                  tinyMceRichText={contentTinyMce}
                />
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
      <div className={clsx(styles["wrapper-wide"], styles["wrapper-articles"])}>
        <div className={clsx(styles.wrapper, "wrapper")}>
          <Grid row>
            <Grid column sm={12}>
              <div className={styles["event-wrapper"]}>
                {events.length === 1 ? (
                  <EventTile
                    // @ts-expect-error need to fix type
                    model={events[0]}
                    withImage={!!events[0].eventTopImage}
                  />
                ) : (
                  <>
                    {events.length > 1 && (
                      <Slider {...sliderSettings}>
                        {events.map((event) => (
                          <SwiperSlide
                            key={event.eventTitle}
                            className={styles.tile}
                          >
                            <EventTile
                              // @ts-expect-error need to fix type
                              model={event}
                              withImage={!!event.eventTopImage}
                            />
                          </SwiperSlide>
                        ))}
                      </Slider>
                    )}
                  </>
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
      <div className={clsx(styles.wrapper, styles["cta-container"], "wrapper")}>
        <Grid row center>
          <Grid column sm={8} md={3}>
            <p>
              {seeAllEventsLink && (
                <Cta type="primary" {...seeAllEventsLink.fields} />
              )}
            </p>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default NavigationalEventsListing;
